package app.nestbridge.app.presenter

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue

import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.karawek.core.auth.model.AuthenticatedUser
import com.karawek.core.auth.screen.AuthenticationResult
import com.karawek.core.auth.screen.AuthenticationScreen
import app.nestbridge.app.usecase.LoadUserContextUseCase
import app.nestbridge.app.usecase.ReadAuthSessionUseCase
import app.nestbridge.app.screen.AppEvent
import app.nestbridge.app.screen.AppScreen
import app.nestbridge.app.screen.AppState
import app.nestbridge.feature.children.screen.ChildListResult
import app.nestbridge.feature.children.screen.ChildListScreen
import app.nestbridge.feature.invitations.screen.InvitationListResult
import app.nestbridge.feature.invitations.screen.InvitationListScreen
import app.nestbridge.feature.main.screen.MainShellScreen
import app.nestbridge.feature.profile.screen.CreateUserProfileScreen
import app.nestbridge.feature.profile.screen.CreateUserProfileUserInfo

import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = AppScreen::class,
    scope = AppScope::class,
)
@Inject
class AppPresenter(
    @Assisted private val navigator: Navigator,
    private val readAuthSessionUseCase: ReadAuthSessionUseCase,
    private val loadUserContextUseCase: LoadUserContextUseCase,
) : Presenter<AppState> {

    @Composable
    override fun present(): AppState {
        val scope = rememberCoroutineScope()
        var authenticatedUser by remember { mutableStateOf<AuthenticatedUser?>(null) }
        var isInitialSetupComplete by remember { mutableStateOf(false) }

        val authNavigator = rememberAnsweringNavigator<AuthenticationResult>(navigator) { result ->
            when (result.status) {
                AuthenticationResult.Status.Authenticated -> {
                    authenticatedUser = result.userInfo
                }
                AuthenticationResult.Status.Cancelled -> Unit
            }
        }

        val childListNavigator = rememberAnsweringNavigator<ChildListResult>(navigator) {
            // Regardless of result, move to main shell
            navigator.goTo(MainShellScreen)
            isInitialSetupComplete = true
        }

        val invitationListNavigator = rememberAnsweringNavigator<InvitationListResult>(navigator) {
            // Regardless of result, move to child list
            childListNavigator.goTo(ChildListScreen)
            isInitialSetupComplete = true
        }

        fun onEvent(
            event: AppEvent,
        ) {
            when (event) {
                AppEvent.OnAppear -> scope.launchSafely {
                    val session = readAuthSessionUseCase()
                    if (session == null) {
                        authNavigator.goTo(AuthenticationScreen())
                        return@launchSafely
                    }

                    val currentContext = loadUserContextUseCase(
                        session = session,
                        authenticatedUser = authenticatedUser
                    )

                    // Skip navigation if we've already completed the initial setup flow
                    if (isInitialSetupComplete) {
                        return@launchSafely
                    }

                    // Check if user profile exists via UserContext
                    if (currentContext?.userProfile == null) {
                        navigator.goTo(
                            CreateUserProfileScreen(
                                createUserProfileUserInfo = authenticatedUser?.let { CreateUserProfileUserInfo.from(it) },
                            ),
                        )
                        authenticatedUser = null
                        return@launchSafely
                    }

                    // User profile exists, go to invitations list
                    invitationListNavigator.goTo(InvitationListScreen())
                }
            }
        }

        return AppState(
            eventSink = ::onEvent,
        )
    }
}
